const text = (t, key, defaultValue, values = {}) =>
  t(`billing.cost_v822.${key}`, { defaultValue, ...values });

const money = (value) =>
  value != null && /^-?\d+(\.\d+)?$/.test(String(value)) ? String(value) : null;

const isZero = (value) => value != null && /^-?0+(\.0+)?$/.test(value);

const count = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

const reasonMessages = {
  provider_cost_profile_unavailable: 'Supplier cost profile is not configured.',
  provider_price_unconfigured: 'Supplier price is not configured.',
  provider_meter_unconfigured:
    'Supplier price is missing a required usage meter.',
  provider_original_price_meter_unconfigured:
    'Supplier baseline price is missing a required usage meter.',
  provider_usage_incomplete: 'Supplier usage is incomplete.',
  provider_service_tier_unconfigured:
    'Supplier service tier is not configured.',
  provider_service_tier_cost_policy_unconfigured:
    'Supplier service tier has no cost policy.',
  provider_channel_config_invalid: 'Supplier channel configuration is invalid.',
  provider_channel_is_not_vertex:
    'This supplier is outside the current internal cost policy.',
  provider_outside_vertex_group:
    'This supplier is outside the current internal cost policy.',
  internal_provider_cost_unresolved: 'Supplier cost could not be resolved.',
};

const unpricedReasons = new Set([
  'provider_cost_profile_unavailable',
  'provider_price_unconfigured',
  'provider_meter_unconfigured',
  'provider_original_price_meter_unconfigured',
  'provider_service_tier_cost_policy_unconfigured',
]);

export const buildBillingLedgerCostV822 = (entry, t) => {
  const status = entry.provider_cost_status || '';
  const reason = entry.provider_cost_unresolved_reason || '';
  const amount = money(entry.provider_cost_usd);
  const exactAmount = money(entry.provider_cost_nano_usd);
  const zero = isZero(exactAmount ?? amount);
  const legacyPriced =
    !status &&
    Number(entry.provider_cost_profile_id) > 0 &&
    Number(entry.provider_price_version_id) > 0;
  const applicable =
    status || reason || entry.entry_type === 'request_charge' || legacyPriced;
  if (!applicable) {
    return {
      state: 'not_applicable',
      value: text(t, 'not_applicable', 'Not applicable'),
      hint: '',
    };
  }
  if (
    !reason &&
    entry.usage_source !== 'missing' &&
    amount != null &&
    (status === 'priced' || (status === 'no_charge' && zero) || legacyPriced)
  ) {
    return {
      state: zero ? 'zero' : 'known',
      value: `USD ${amount}`,
      hint: zero
        ? status === 'no_charge'
          ? text(t, 'no_charge', 'Explicitly not charged by supplier')
          : exactAmount != null
          ? text(t, 'recorded_zero', 'Confirmed recorded zero cost')
          : text(
              t,
              'displayed_zero',
              'Recorded cost is zero at displayed USD precision.'
            )
        : isZero(amount) && exactAmount != null
        ? text(
            t,
            'rounded_cost',
            'Rounded display; recorded cost: {{amount}} nano-USD.',
            { amount: exactAmount }
          )
        : '',
    };
  }
  const state =
    status === 'unpriced' || unpricedReasons.has(reason)
      ? 'unpriced'
      : 'unknown';
  let hint;
  if (reason) {
    hint = reasonMessages[reason]
      ? text(t, `reason.${reason}`, reasonMessages[reason])
      : text(t, 'reason_code', 'Recorded cost reason: {{reason}}', { reason });
  } else if (entry.usage_source === 'missing') {
    hint = text(
      t,
      'reason.provider_usage_incomplete',
      reasonMessages.provider_usage_incomplete
    );
  } else {
    hint = text(
      t,
      'missing_evidence',
      'No complete supplier-cost evidence is recorded.'
    );
  }
  return {
    state,
    value:
      state === 'unpriced'
        ? text(t, 'unpriced', 'Unpriced')
        : text(t, 'unknown', 'Unknown'),
    hint,
  };
};

export const buildBillingAggregateCostV822 = (data, scope, t) => {
  const prefix = scope === 'summary' ? 'cycle_' : '';
  const amount = money(data[`${prefix}provider_cost_usd`]);
  const baseline = money(data[`${prefix}provider_baseline_cost_usd`]);
  const profit = money(data[`${prefix}gross_profit_usd`]);
  const completeFlag = data[`${prefix}gross_profit_complete`];
  const separateCounts = scope === 'profitability';
  const unknownRequests = count(data.unknown_cost_request_count);
  const unknownAttempts = count(data[`${prefix}unknown_attempt_cost_count`]);
  const knownRequests = count(data.known_cost_request_count);
  const countsAvailable = separateCounts
    ? unknownRequests != null && unknownAttempts != null
    : unknownAttempts != null;
  const unknownCount = countsAvailable
    ? (separateCounts ? unknownRequests : 0) + unknownAttempts
    : null;
  const complete =
    completeFlag !== false &&
    (separateCounts
      ? unknownRequests === 0 && unknownAttempts === 0
      : completeFlag === true &&
        (unknownAttempts === 0 ||
          data[`${prefix}unknown_attempt_cost_count`] == null));
  const knownEvidence =
    (knownRequests != null && knownRequests > 0) ||
    (amount != null && !isZero(amount));
  const state =
    complete && amount != null
      ? isZero(amount)
        ? 'zero'
        : 'known'
      : knownEvidence
      ? 'partial'
      : 'unknown';
  const unknown = text(t, 'unknown', 'Unknown');
  const partial = (value) =>
    text(t, 'partial', 'Partially known: USD {{amount}}', { amount: value });
  const value =
    state === 'known' || state === 'zero'
      ? `USD ${amount}`
      : state === 'partial' && amount != null
      ? partial(amount)
      : unknown;
  let hint = '';
  if (!complete) {
    hint = countsAvailable
      ? separateCounts
        ? text(
            t,
            'separate_counts',
            'Unresolved ledger requests: {{requests}}; unpriced extra attempts: {{attempts}}. These are not a unique-request total.',
            { requests: unknownRequests, attempts: unknownAttempts }
          )
        : text(
            t,
            'combined_count',
            '{{count}} unresolved ledger / unpriced extra-attempt cost records (combined; not unique requests).',
            { count: unknownCount }
          )
      : text(
          t,
          'incomplete',
          'Supplier cost is incomplete; profit is unknown.'
        );
    if (unknownCount === 0) {
      hint = text(
        t,
        'incomplete',
        'Supplier cost is incomplete; profit is unknown.'
      );
    }
  } else if (state === 'zero') {
    hint = text(
      t,
      'complete_zero',
      'Complete cost total is zero at displayed USD precision.'
    );
  } else if (amount == null) {
    hint = text(
      t,
      'missing_evidence',
      'No complete supplier-cost evidence is recorded.'
    );
  }
  const profitComplete = complete && amount != null && profit != null;
  return {
    state,
    complete: complete && amount != null,
    value,
    hint,
    baselineValue:
      baseline == null
        ? unknown
        : complete
        ? `USD ${baseline}`
        : knownEvidence || !isZero(baseline)
        ? partial(baseline)
        : unknown,
    profitValue: profitComplete ? `USD ${profit}` : unknown,
    profitHint: profitComplete
      ? scope === 'profitability'
        ? data.gross_margin_percent == null
          ? t('billing.profitability.margin_unavailable')
          : `${data.gross_margin_percent}%`
        : ''
      : [
          hint ||
            text(
              t,
              'incomplete',
              'Supplier cost is incomplete; profit is unknown.'
            ),
          profit != null
            ? separateCounts
              ? text(
                  t,
                  'known_portion_difference',
                  'Known-cost portion difference only: USD {{amount}} (not total profit).',
                  { amount: profit }
                )
              : text(
                  t,
                  'provisional_difference',
                  'Provisional difference: USD {{amount}} (not confirmed profit).',
                  { amount: profit }
                )
            : '',
        ]
          .filter(Boolean)
          .join(' '),
  };
};
